import * as graphql from 'generated/graphql';
import { useModalLink } from 'navigation/ModalNavigation';
import SidebarSectionHeader from './SectionHeader';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { CardList, SegmentedControl } from '@treams/treams-ui';
import { Box, Flex } from 'reflexbox';
import { useLocation } from 'react-router';
import { DraftsIcon } from '@treams/treams-icons';
import NoteCard from 'notes/NoteCard';

function CompactSharedNotesList(props: { userID: string; otherID: string }) {
	const location = useLocation();

	const notes = graphql.useMutualNotesQuery({
		variables: {
			first: 5, // I'm not sure if this is doing anything since the mutualNotes query doesn't use this variable
			id: props.otherID,
			filters: { showArchived: false },
		},
	});

	useEffect(() => {
		notes.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return (
		<CardList
			translationName="notes.index"
			query={notes}
			path="user.mutualNotes"
			icon={<DraftsIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
		/>
	);
}

function CompactPrivateNotesList(props: { userID: string }) {
	const location = useLocation();

	const notes = graphql.useNotesQuery({
		variables: {
			first: 5,
			filters: {
				isArchived: false,
			},
		},
	});

	useEffect(() => {
		notes.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return (
		<CardList
			translationName="notes.index"
			query={notes}
			path="notes"
			icon={<DraftsIcon />}
			renderCard={(item: graphql.Note) => (
				<NoteCard userID={props.userID} note={item} compact modal />
			)}
			renderSkeleton={() => <NoteCard.Skeleton compact />}
			compact
		/>
	);
}

export default function SidebarNotesSection(props: {
	userID: string;
	conversation: graphql.Conversation;
}) {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState(false);

	const [tab, setTab] = useState<'shared' | 'private'>('shared');

	const isRequester = props.conversation.viewer.user.id === props.conversation.requester.id;
	const otherID = isRequester
		? props.conversation.requester.id
		: props.conversation.participants.find(
				(participant) => participant.role === graphql.ConversationRole.Participant,
		  )!.user.id;

	const link = useModalLink(tab === 'shared' ? `/notes/new/${otherID}` : '/notes/new');

	return (
		<>
			<SidebarSectionHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} createLink={link}>
				{t('conversation.sidebar.notes.title')}
			</SidebarSectionHeader>

			{isOpen && (
				<Box mb={3}>
					<Flex mb={2}>
						<SegmentedControl
							options={[
								{ title: t('conversation.peek.notes.shared'), value: 'shared' },
								{ title: t('conversation.peek.notes.private'), value: 'private' },
							]}
							value={tab}
							onChange={(value) => setTab(value as 'shared' | 'private')}
						/>
					</Flex>

					{tab === 'shared' && <CompactSharedNotesList userID={props.userID} otherID={otherID} />}
					{tab === 'private' && <CompactPrivateNotesList userID={props.userID} />}
				</Box>
			)}
		</>
	);
}
